import { Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { FunnelTotals } from '@entities/analytics';

const { Text, Title } = Typography;

const PCT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });

export function Funnel({ data }: { data: FunnelTotals }) {
  const { t } = useTranslation();
  const steps = [
    { key: 'views', color: '#2563eb', value: data.views, conv: null as number | null },
    { key: 'chats', color: '#10b981', value: data.chats, conv: data.viewsToChats },
    { key: 'deals', color: '#8b5cf6', value: data.deals, conv: data.chatsToDeals },
  ] as const;
  const max = steps.reduce((acc, s) => Math.max(acc, s.value), 0) || 1;

  return (
    <Card
      title={<Title level={5} style={{ margin: 0 }}>{t('dashboard.funnel.title')}</Title>}
      extra={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('dashboard.funnel.overall', { value: PCT.format(data.overall) })}
        </Text>
      }
      bordered
      style={{ borderRadius: 12 }}
      bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 20 }}
    >
      {steps.map((step) => {
        const width = Math.max(8, Math.round((step.value / max) * 100));
        return (
          <div key={step.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontWeight: 500 }}>{t(`dashboard.funnel.${step.key}`)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {step.conv == null ? '—' : PCT.format(step.conv)}
              </Text>
            </div>
            <div
              style={{
                position: 'relative',
                height: 28,
                borderRadius: 8,
                background: 'var(--ant-color-fill-tertiary)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${width}%`,
                  background: `linear-gradient(90deg, ${step.color}, ${step.color}cc)`,
                  borderRadius: 8,
                  transition: 'width 300ms ease',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingInlineEnd: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--ant-color-text)',
                }}
              >
                {step.value.toLocaleString()}
              </span>
            </div>
          </div>
        );
      })}
    </Card>
  );
}
